import { BN } from "@utils/BN";

export type Side = "long" | "short";

export interface UseLiqPrice {
  calc: (params: {
    entryPrice: BN;
    leverage: number;
    mmr: BN;
    size: BN;
    extraMargin?: BN;
    feeToClose?: BN;
    side?: Side;
  }) => BN;
}

/**
 * Calculates the liquidation price for a **linear** product using the formula:
 *   M_liq = (Balance + F - E*S) / (|S|*mmr - S)
 *
 * Where:
 * - Balance = position margin (positionValue / leverage + extraMargin)
 * - E = entry price
 * - S = signed size (positive for long, negative for short)
 * - mmr = maintenance margin ratio
 * - F = funding (0 for new orders)
 */
const useLiqPrice = (): UseLiqPrice => {
  const calc: UseLiqPrice["calc"] = ({ entryPrice, leverage, mmr, size, extraMargin = BN.ZERO, side = "long" }): BN => {
    if (size.isZero()) return BN.ZERO;

    const L = new BN(leverage).isZero() ? new BN(1) : new BN(leverage);

    // Signed size: positive for long, negative for short
    const signedSize = side === "long" ? size : size.negated();

    // Position value
    const positionValue = size.multipliedBy(entryPrice);

    // Balance = initial margin + extra margin
    const balance = positionValue.dividedBy(L).plus(extraMargin);

    // Calculate denominator: |S| * mmr - S
    const absS = signedSize.abs();
    const denom = absS.multipliedBy(mmr).minus(signedSize);

    if (denom.isZero()) return BN.ZERO;

    // M_liq = (Balance - E*S) / (|S|*mmr - S)
    const numerator = balance.minus(entryPrice.multipliedBy(signedSize));
    const liqPrice = numerator.dividedBy(denom);

    // Return 0 if negative
    return liqPrice.isLessThan(0) ? BN.ZERO : liqPrice;
  };

  return { calc };
};

export { useLiqPrice };
